import { serialize as objectToFormData } from 'object-to-formdata';
import { cloneDeep, uniqueId } from 'lodash';

class ComparisonOptions {
  exclude = [''];
  deep = false;
}

class FormSerializationOptions {
  indices = true;
  nullsAsUndefineds = true;
}

export const equals = (from, to, options = new ComparisonOptions()) => {
  if (from === to) return true;
  if ([from, to].includes(null)) return false;
  if ([from, to].includes(undefined)) return false;
  if ([typeof from, typeof to].some((x) => x !== 'object')) return false;

  const obj1 = copy(from);
  const obj2 = copy(to);
  const { exclude, deep } = options;
  const keys1 = Object.keys(obj1).filter((x) => !exclude.includes(x));
  const keys2 = Object.keys(obj2).filter((x) => !exclude.includes(x));

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (!Object.hasOwn(obj2, key)) return false;
    const val1 = obj1[key];
    const val2 = obj2[key];
    const valOptions = deep ? options : new ComparisonOptions();
    if (!equals(val1, val2, valOptions)) return false;
  }

  return true;
};
export const copy = (obj) => {
  if (obj instanceof File || obj instanceof Date) return obj;
  return cloneDeep(obj);
};
export const diff = (from, to, options = new ComparisonOptions()) =>
  !equals(from, to, options);
export const moneyOptions = Object.freeze({
  decimal: '.',
  thousands: ',',
  prefix: '$',
  precision: 2,
});
export const match = (value, options) => options[value];
export const differences = (from, to, options = new ComparisonOptions()) => {
  if (from === to) return { equals: true, differences: {} };
  if (!from || !to || typeof from !== 'object' || typeof to !== 'object') {
    return { equals: false, differences: { value: { from, to } } };
  }

  const { exclude, deep } = options;
  const obj1 = copy(from);
  const obj2 = copy(to);
  const keys = [
    ...new Set([...Object.keys(obj1), ...Object.keys(obj2)]),
  ].filter((x) => !exclude.includes(x));

  const differences = Object.fromEntries(
    keys
      .map((key) => {
        const has1 = Object.hasOwn(obj1, key);
        const has2 = Object.hasOwn(obj2, key);
        if (!has1 || !has2)
          return [
            key,
            {
              from: has1 ? obj1[key] : undefined,
              to: has2 ? obj2[key] : undefined,
            },
          ];

        const val1 = obj1[key];
        const val2 = obj2[key];
        if (
          deep &&
          val1 &&
          val2 &&
          typeof val1 === 'object' &&
          typeof val2 === 'object'
        ) {
          const nested = diff(val1, val2, options);
          return nested.equals ? null : [key, nested.differences];
        }

        return equals(val1, val2, new ComparisonOptions())
          ? null
          : [key, { from: val1, to: val2 }];
      })
      .filter(Boolean)
  );

  return { equals: Object.keys(differences).length === 0, differences };
};
export const key = (prefix = undefined) => uniqueId(prefix);

export const serialize = (value, options = new FormSerializationOptions()) =>
  objectToFormData(copy(value), options);

export default Object.freeze({
  equals,
  copy,
  diff,
  match,
  differences,
  key,
  serialize,
});
